import { http } from '@/utils/httpClient'
import type * as XLSX from 'xlsx'
import type * as Papa from 'papaparse'

export interface AccessibilityReport {
  id: string
  report_date: string
  report_name: string
  uploaded_by: string
  uploaded_by_name: string
  uploaded_at: string
  file_info: {
    original_name: string
    file_size: number
    row_count: number
    processing_time_ms?: number
  }
  summary: {
    total_issues: number
    by_impact: {
      critical: number
      serious: number
      moderate: number
      minor: number
    }
    by_status: {
      open: number
      in_progress: number
      blocked: number
      closed: number
    }
    by_assignee?: {
      unassigned: number
      [key: string]: number
    }
    unique_pages: number
    unique_tests: number
    top_pages: Array<{ name: string; count: number }>
    top_tests: Array<{ name: string; count: number }>
    comparison: {
      previous_report_date: string
      new_issues: number
      fixed_issues: number
      recurring_issues: number
      net_change: number
    } | null
  }
  processing_status: 'processing' | 'completed' | 'failed'
  error_message?: string
  is_deleted: boolean
  created_at: string
  updated_at: string
}

export interface AccessibilityIssue {
  id: string
  fingerprint: string
  test_name: string
  page_url: string
  issue_id: string
  impact: 'critical' | 'serious' | 'moderate' | 'minor'
  description: string
  help_url: string
  node: string
  first_detected: string
  last_detected: string
  report_dates: string[]
  status: 'open' | 'in_progress' | 'blocked' | 'closed'
  assigned_to?: string
  assigned_to_name?: string
  assigned_date?: string
  assigned_by?: string
  assigned_by_name?: string
  assignment_history?: Array<{
    assigned_to: string
    assigned_to_name: string
    assigned_by: string
    assigned_by_name: string
    assigned_date: string
  }>
  is_deleted: boolean
  created_at: string
  updated_at: string
}

export interface QueryIssuesParams {
  report_date?: string
  start_date?: string
  end_date?: string
  impacts?: string[]
  statuses?: string[]
  page_url?: string
  test_name?: string
  issue_id?: string
  node?: string
  assigned_to?: string[]
  search?: string
  page?: number
  limit?: number
  sort_by?: string
  sort_order?: 'asc' | 'desc'
}

export interface PaginatedIssues {
  data: AccessibilityIssue[]
  pagination: {
    total: number
    page: number
    limit: number
    pages: number
  }
}

export interface AccessibilityStatistics {
  total_issues: number
  by_impact: {
    critical: number
    serious: number
    moderate: number
    minor: number
  }
  by_status: {
    open: number
    in_progress: number
    blocked: number
    closed: number
  }
  by_assignee: {
    unassigned: number
    [key: string]: number
  }
  unique_pages: number
  unique_tests: number
}

class AccessibilityService {
  private readonly baseUrl = '/accessibility'

  /**
   * Validate accessibility report(s) by parsing on frontend
   * This bypasses file upload size limits by sending parsed JSON data instead of raw files
   */
  async validateReport(files: File[]): Promise<{
    total_rows: number;
    valid_rows: number;
    invalid_rows: number;
    unique_issues: number;
    duplicate_rows: number;
    by_impact: { critical: number; serious: number; moderate: number; minor: number };
    sample_invalid_reasons: string[];
  }> {
    // Validate files before parsing
    if (!files || files.length === 0) {
      throw new Error('No files provided for validation')
    }
    
    // Check if files have content
    for (const file of files) {
      if (file.size === 0) {
        throw new Error(`File "${file.name}" is empty (0 bytes)`)
      }
    }
    
    // Parse files on frontend (same approach as upload to avoid file size limits)
    const parsedData: any[] = []
    
    for (const file of files) {
      const fileData = await this.parseFileOnFrontend(file)
      parsedData.push(...fileData)
    }
    
    // Send data in batches to avoid hitting body size limits
    // Even JSON data can be too large if there are many rows
    const BATCH_SIZE = 1000 // Process 1000 rows at a time
    const batches = []
    
    for (let i = 0; i < parsedData.length; i += BATCH_SIZE) {
      batches.push(parsedData.slice(i, i + BATCH_SIZE))
    }
    
    // Send batches and aggregate results
    let totalValidRows = 0
    let totalInvalidRows = 0
    const allInvalidReasons: string[] = []
    const impactCounts = { critical: 0, serious: 0, moderate: 0, minor: 0 }
    
    for (let i = 0; i < batches.length; i++) {
      const batchResult = await http.post(`${this.baseUrl}/validate-parsed`, {
        data: batches[i]
      })
      
      totalValidRows += batchResult.valid_rows
      totalInvalidRows += batchResult.invalid_rows
      
      // Aggregate impact counts
      impactCounts.critical += batchResult.by_impact.critical
      impactCounts.serious += batchResult.by_impact.serious
      impactCounts.moderate += batchResult.by_impact.moderate
      impactCounts.minor += batchResult.by_impact.minor
      
      // Collect sample invalid reasons (first batch only)
      if (i === 0 && batchResult.sample_invalid_reasons) {
        allInvalidReasons.push(...batchResult.sample_invalid_reasons)
      }
    }
    
    return {
      total_rows: parsedData.length,
      valid_rows: totalValidRows,
      invalid_rows: totalInvalidRows,
      unique_issues: totalValidRows, // Approximation - actual dedup happens on upload
      duplicate_rows: 0, // Can't calculate across batches without backend support
      by_impact: impactCounts,
      sample_invalid_reasons: allInvalidReasons.slice(0, 5)
    }
  }

  async uploadReport(
    files: File[],
    reportDate: Date | number | string,
    onProgress?: (info: { percentage: number; stage: 'parsing' | 'uploading' | 'completed'; current?: number; total?: number }) => void
  ): Promise<AccessibilityReport> {
    // Validate files before uploading
    if (!files || files.length === 0) {
      throw new Error('No files provided for upload')
    }
    
    // Check if files have content
    for (const file of files) {
      if (file.size === 0) {
        throw new Error(`File "${file.name}" is empty (0 bytes)`)
      }
    }
    
    // Parse files on frontend (same as test cases import)
    const parsedData: any[] = []
    
    // Parsing progress: 0% -> 20%
    onProgress?.({ percentage: 0, stage: 'parsing', current: 0, total: files.length })
    for (let i = 0; i < files.length; i++) {
      const file = files[i]
      const fileData = await this.parseFileOnFrontend(file)
      parsedData.push(...fileData)
      const pct = Math.min(20, Math.round(((i + 1) / files.length) * 20))
      onProgress?.({ percentage: pct, stage: 'parsing', current: i + 1, total: files.length })
    }
    
    // Track total rows parsed from frontend (this is the accurate count)
    const totalParsedRows = parsedData.length
    
    // Validate that file(s) contain data rows (not just headers)
    if (totalParsedRows === 0) {
      throw new Error('No data rows found in the uploaded file(s). The file may contain only headers or be empty.')
    }
    
    // Convert reportDate to Date object if needed, then to ISO string
    const dateObj = reportDate instanceof Date ? reportDate : new Date(reportDate)
    const reportDateISO = dateObj.toISOString()
    
    // Split into batches to avoid hitting body size limits
    const BATCH_SIZE = 1000
    const batches = []
    
    for (let i = 0; i < parsedData.length; i += BATCH_SIZE) {
      batches.push(parsedData.slice(i, i + BATCH_SIZE))
    }
    
    let reportId: string | undefined
    let finalReport: any
    
    // Uploading progress: 20% -> 100%
    onProgress?.({ percentage: 20, stage: 'uploading', current: 0, total: batches.length })
    // Send batches sequentially
    for (let i = 0; i < batches.length; i++) {
      const response = await http.post(`${this.baseUrl}/upload-parsed`, {
        data: batches[i],
        report_date: reportDateISO,
        batch_info: {
          current: i + 1,
          total: batches.length,
          report_id: reportId,
          total_rows: totalParsedRows // Pass total rows to backend for accurate tracking
        }
      })
      
      // First batch returns the report_id
      if (i === 0) {
        reportId = response.report_id || response.id
      }
      
      // Last batch returns the complete report
      if (i === batches.length - 1) {
        finalReport = response
      }

      const pct = 20 + Math.round(((i + 1) / batches.length) * 80)
      onProgress?.({ percentage: Math.min(100, pct), stage: 'uploading', current: i + 1, total: batches.length })
    }
    
    // Validate that we received a final report from the backend
    if (!finalReport) {
      throw new Error('Failed to receive final report from server after uploading all batches')
    }

    // Trust backend's row_count - it reflects only validated rows (excludes filtered invalid rows)
    // Do not overwrite with totalParsedRows which includes all parsed rows including invalid ones

    onProgress?.({ percentage: 100, stage: 'completed', current: batches.length, total: batches.length })
    return finalReport
  }
  
  /**
   * Parse CSV/Excel file on frontend (same as test cases import)
   */
  private async parseFileOnFrontend(file: File): Promise<any[]> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      
      reader.onload = async (e) => {
        try {
          const fileName = file.name.toLowerCase()
          
          if (fileName.endsWith('.csv')) {
            // Parse CSV using Papa Parse (same as backend)
            const Papa = (await import('papaparse')).default
            const text = e.target?.result as string
            const result = Papa.parse(text, {
              header: true,
              skipEmptyLines: true
            })
            resolve(result.data)
          } else if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) {
            // Parse Excel using XLSX (same as test cases import)
            const XLSX = (await import('xlsx')).default
            const data = new Uint8Array(e.target?.result as ArrayBuffer)
            const workbook = XLSX.read(data, { type: 'array' })
            const firstSheetName = workbook.SheetNames[0]
            const worksheet = workbook.Sheets[firstSheetName]
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: '' })
            resolve(jsonData)
          } else {
            reject(new Error('Unsupported file format'))
          }
        } catch (error) {
          reject(error)
        }
      }
      
      reader.onerror = () => reject(new Error('Failed to read file'))
      
      // Read file based on type
      if (file.name.toLowerCase().endsWith('.csv')) {
        reader.readAsText(file)
      } else {
        reader.readAsArrayBuffer(file)
      }
    })
  }

  /**
   * Get all reports
   */
  async getAllReports(): Promise<AccessibilityReport[]> {
    return await http.get(`${this.baseUrl}/reports`)
  }

  /**
   * Get report by ID
   */
  async getReportById(id: string): Promise<AccessibilityReport> {
    return await http.get(`${this.baseUrl}/reports/${id}`)
  }

  /**
   * Delete report (soft delete)
   */
  async deleteReport(id: string): Promise<void> {
    await http.delete(`${this.baseUrl}/reports/${id}`)
  }

  /**
   * Query issues with filters and pagination
   */
  async queryIssues(params: QueryIssuesParams): Promise<PaginatedIssues> {
    // Convert params to query string
    const queryParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          queryParams.append(key, value.join(','))
        } else {
          queryParams.append(key, String(value))
        }
      }
    })
    
    const queryString = queryParams.toString()
    const url = `${this.baseUrl}/issues${queryString ? `?${queryString}` : ''}`
    
    return await http.get(url)
  }

  /**
   * Get statistics
   */
  async getStatistics(startDate?: Date, endDate?: Date, filters?: any): Promise<AccessibilityStatistics> {
    const queryParams = new URLSearchParams()
    if (startDate) queryParams.append('start_date', startDate.toISOString())
    if (endDate) queryParams.append('end_date', endDate.toISOString())
    
    // Add filter parameters (NestJS accepts multiple values with same param name)
    if (filters) {
      if (filters.impacts && filters.impacts.length > 0) {
        filters.impacts.forEach((impact: string) => queryParams.append('impacts', impact))
      }
      if (filters.statuses && filters.statuses.length > 0) {
        filters.statuses.forEach((status: string) => queryParams.append('statuses', status))
      }
      if (filters.page_urls && filters.page_urls.length > 0) {
        filters.page_urls.forEach((url: string) => queryParams.append('page_urls', url))
      }
      if (filters.test_names && filters.test_names.length > 0) {
        filters.test_names.forEach((name: string) => queryParams.append('test_names', name))
      }
      if (filters.issue_ids && filters.issue_ids.length > 0) {
        filters.issue_ids.forEach((id: string) => queryParams.append('issue_ids', id))
      }
      if (filters.nodes && filters.nodes.length > 0) {
        filters.nodes.forEach((node: string) => queryParams.append('nodes', node))
      }
      if (filters.assigned_to && filters.assigned_to.length > 0) {
        filters.assigned_to.forEach((assignee: string) => queryParams.append('assigned_to', assignee))
      }
      if (filters.search) {
        queryParams.append('search', filters.search)
      }
    }
    
    const queryString = queryParams.toString()
    const url = `${this.baseUrl}/statistics${queryString ? `?${queryString}` : ''}`
    
    return await http.get(url)
  }

  /**
   * Query all issues without pagination (for export)
   */
  async queryAllIssues(params: QueryIssuesParams): Promise<AccessibilityIssue[]> {
    const queryParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          queryParams.append(key, value.join(','))
        } else {
          queryParams.append(key, String(value))
        }
      }
    })
    
    const queryString = queryParams.toString()
    const url = `${this.baseUrl}/issues/all${queryString ? `?${queryString}` : ''}`
    
    return await http.get(url)
  }

  /**
   * Assign issues to a user
   */
  async assignIssues(
    issueIds: string[],
    assignedTo: string | null,
    assignedToName: string | null,
    assignedBy: string,
    assignedByName: string
  ): Promise<void> {
    await http.post(`${this.baseUrl}/assign`, {
      issue_ids: issueIds,
      assigned_to: assignedTo,
      assigned_to_name: assignedToName,
      assigned_by: assignedBy,
      assigned_by_name: assignedByName
    })
  }

  /**
   * Update status of issues
   */
  async updateStatus(issueIds: string[], status: string, updatedBy?: string, updatedByName?: string): Promise<void> {
    await http.post(`${this.baseUrl}/update-status`, {
      issue_ids: issueIds,
      status,
      updated_by: updatedBy,
      updated_by_name: updatedByName
    })
  }

  /**
   * Get assignment history for an issue
   */
  async getAssignmentHistory(issueId: string): Promise<any> {
    return await http.get(`${this.baseUrl}/assignment-history/${issueId}`)
  }

  /**
   * Get test summary (grouped by test_name with status counts)
   */
  async getTestSummary(reportDate?: Date): Promise<any[]> {
    const params: any = {}
    if (reportDate) {
      params.report_date = reportDate.toISOString()
    }
    return await http.get(`${this.baseUrl}/test-summary`, { params })
  }

  /**
   * Get accessibility trends over time
   */
  async getTrends(startDate?: Date, endDate?: Date): Promise<any> {
    const queryParams = new URLSearchParams()
    if (startDate) queryParams.append('start_date', startDate.toISOString())
    if (endDate) queryParams.append('end_date', endDate.toISOString())
    
    const queryString = queryParams.toString()
    const url = `${this.baseUrl}/trends${queryString ? `?${queryString}` : ''}`
    
    return await http.get(url)
  }
}

export const accessibilityService = new AccessibilityService()

